import * as React from 'react';
import parse from 'html-react-parser';
import { PageImages, PageVideos } from 'lib/types';
import { ImageBlock, Block, BlogEntry } from 'cms/types';
import ImageWrapper from 'core/components/ImageWrapper';
import * as styles from './BlogContent.module.scss';
import LinkBlock from '../LinkBlock';
import ImageWithText from '../ImageWithText';
import BookingButton from '../BookingButton';

export type BlogContentProps = {
    entry: BlogEntry & {
        imageWithTextImages: PageImages;
        imageWithTextImagesFull: PageImages;
    };
    emailBanner?: React.ReactElement;
};

type SelectableComponentsType = {
    [key: string]: {
        label: string;
        component: (
            block: Block,
            options?: {
                index?: number;
                images?: { [key: string]: PageImages };
                videos?: { [key: string]: PageVideos };
            },
        ) => React.ReactNode;
    };
};

const selectableComponents: SelectableComponentsType = {
    paragraph: {
        label: 'Paragraph',
        component: (block: Block) => {
            return parse(block.data?.text || '');
        },
    },
    linkBlock: {
        label: 'Link Block',
        component: (block: Block) => (
            <LinkBlock text={parse(block.data?.text || '')} />
        ),
    },
    bookingButton: {
        label: 'Booking Button',
        component: (block: Block) => (
            <BookingButton text={parse(block.data?.text || '')} />
        ),
    },
    imageWithText: {
        label: 'Image',
        component: (block: Block<ImageBlock>, options) => {
            const images =
                block.data.type === 'full'
                    ? options?.images?.full
                    : options?.images?.small;
            const imageObj = images?.find(
                (img) => img.slug === `${options?.index}.data.blockImage`,
            );
            return (
                <>
                    <ImageWithText
                        imagePosition={block.data?.type || 'left'}
                        text={parse(block.data?.text || '')}
                        description={block.data?.description}
                        image={
                            <ImageWrapper
                                image={imageObj?.image}
                                alt={block.data?.blockImage?.alt ?? 'no'}
                            />
                        }
                    />
                </>
            );
        },
    },
};

function BlogContent({
    entry: { content, imageWithTextImages, imageWithTextImagesFull },
    emailBanner,
}: BlogContentProps): React.ReactElement {
    const elements = React.useMemo(() => {
        if (content) {
            const html = content.map((block, index) => {
                if (block.blockType === 'imageWithText') {
                    return selectableComponents[block.blockType].component(
                        block,
                        {
                            index,
                            images: {
                                small: imageWithTextImages,
                                full: imageWithTextImagesFull,
                            },
                        },
                    );
                }
                return selectableComponents[block.blockType].component(block);
            });
            html.splice(1, 0, emailBanner);
            return html;
        }

        return null;
    }, [content, imageWithTextImages, imageWithTextImagesFull, emailBanner]);
    return <section className={styles.root}>{elements}</section>;
}

export default BlogContent;
